import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Auth } from "../utils/api";
import { useAuth } from "../auth/useAuth";
import { MENU, hasAny } from "../auth/acl";

export default function Header() {
  const { me, perms, loading, refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const dropdownRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    function onDocClick(e) {
      if (!dropdownRef.current) return;
      if (!dropdownRef.current.contains(e.target)) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  async function handleLogout() {
    try { await Auth.logout(); } finally {
      await refresh();
      setOpen(false);
      setMobileOpen(false);
      nav("/login", { replace: true });
    }
  }

  const visible = MENU.filter(it => hasAny(perms, it.rights));
  const navLink =
    "block px-3 py-1 rounded-md text-sm text-gray-800 hover:bg-red-50 hover:text-red-700 transition";

  return (
    <header className="sticky top-0 z-50 text-white bg-gradient-to-r from-red-700 via-red-600 to-red-700 border-b border-red-700/40">
      <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
        {/* Logo + Title + Phone (trái) */}
        <div className="flex items-center gap-3">
          {/* Đặt file vào public/logo.png */}
          <img
            src="/logo.png"
            alt="Logo"
            className="w-10 h-10 object-contain"
          />
          <div className="leading-tight">
            <Link
              to="/"
              className="block text-base sm:text-xl font-extrabold tracking-tight text-white hover:text-white/90"
            >
              Sân khấu 1122
            </Link>
            <a
              href="tel:0852852741"
              className="block text-[12px] sm:hidden text-white/80 hover:text-white"
            >
              0852.852.741
            </a>
          </div>
        </div>

        {/* Hotline badge (desktop) */}
        <div className="hidden sm:block text-sm text-white/80">
          <a
            href="tel:0852852741"
            className="inline-flex items-center gap-2 font-semibold text-white bg-red-600 px-3 py-1.5 rounded-lg hover:bg-red-700"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M22 16.92v2a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.92 4.18 2 2 0 0 1 4.86 2h2a2 2 0 0 1 2 1.72c.12.9.32 1.77.59 2.6a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.48-1.48a2 2 0 0 1 2.11-.45c.83.27 1.7.47 2.6.59A2 2 0 0 1 22 16.92Z"
                stroke="#fff"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span>Hotline: 0852.852.741</span>
          </a>
        </div>

        {/* User / Login (desktop) */}
        <div className="hidden sm:block relative" ref={dropdownRef}>
          {!loading && me ? (
            <div className="relative">
              <button
                onClick={() => setOpen(v => !v)}
                className="px-3 py-1 rounded-md bg-white/10 hover:bg-white/20 text-white text-sm border border-white/20"
              >
                {(me.username || me.display_name || me.email || "Tài khoản")} ▾
              </button>

              {open && (
                <div className="absolute right-0 mt-2 w-56 bg-white shadow-lg rounded-lg border">
                  <div className="py-2">
                    {visible
                      .filter(i => i.key !== "logout")
                      .map(it => (
                        <NavLink
                          key={it.key}
                          to={it.to}
                          onClick={() => setOpen(false)}
                          className={navLink}
                        >
                          {it.label}
                        </NavLink>
                      ))}
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-3 py-1 text-sm rounded-md text-gray-800 hover:bg-red-50 hover:text-red-700"
                    >
                      Đăng xuất
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : !loading ? (
            <Link
              to="/login"
              className="px-3 py-1.5 rounded-md bg-white text-gray-900 text-sm hover:bg-red-50 hover:text-red-700 shadow-sm"
            >
              Đăng nhập
            </Link>
          ) : null}
        </div>

        {/* Toggle mobile menu */}
        <button
          className="sm:hidden p-2 border border-white/30 rounded-md text-white hover:bg-white/10"
          onClick={() => setMobileOpen(v => !v)}
          aria-label="Mở menu"
        >
          ☰
        </button>
      </div>

      {/* Mobile menu */}
      <div
        className={`sm:hidden overflow-hidden transition-all duration-300 ${mobileOpen ? "max-h-screen" : "max-h-0"
          }`}
      >
        <div className="px-3 py-3 bg-white text-gray-900 border-t shadow-sm">
          {!loading && me ? (
            <div className="grid gap-1">
              {visible
                .filter(i => i.key !== "logout")
                .map(it => (
                  <NavLink
                    key={it.key}
                    to={it.to}
                    onClick={() => setMobileOpen(false)}
                    className="block px-3 py-2 rounded-lg hover:bg-gray-50"
                  >
                    {it.label}
                  </NavLink>
                ))}
              <button
                onClick={handleLogout}
                className="block text-left px-3 py-2 rounded-lg hover:bg-gray-50"
              >
                Đăng xuất
              </button>
            </div>
          ) : !loading ? (
            // Nút đăng nhập mobile: nền đỏ thay vì đen
            <Link
              to="/login"
              onClick={() => setMobileOpen(false)}
              className="block px-3 py-2 rounded-lg bg-red-600 text-white text-center hover:bg-red-700 shadow"
            >
              Đăng nhập
            </Link>
          ) : null}
        </div>
      </div>

      <div className="h-0.5 w-full bg-white/20" />
    </header>
  );
}
